import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Status and metadata communications fields.
 *
 * Each field carries its own `savedViewMembership`; `./communications-saved-views.ts`
 * derives `visibleColumns` from those declarations via `collectFieldsForSavedView`.
 */
export const statusCommunicationsFields = defineFieldConfigMap({
  // FIELD: status
  status: {
    type: "select",
    label: "Status",
    formSpan: 1,
    isStatusField: true,
    options: [
      { label: "Pending", value: "pending" },
      { label: "Completed", value: "completed" },
      { label: "Cancelled", value: "cancelled" },
    ],
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "CheckCircle",
    mobileDetailSectionId: "status",
    groupable: true,
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "status",
    savedViewMembership: [
      "all-communications",
      "type-email",
      "type-note",
      "type-call",
      "type-meeting",
    ],
  },
});

export const metadataCommunicationsFields = defineFieldConfigMap({
  // FIELD: id
  id: {
    type: "text",
    label: "ID",
    formSpan: 1,
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "contains",
    width: 220,
    minWidth: 180,
    maxWidth: 320,
    icon: "Hash",
    mobileDetailSectionId: "system",
  },
  // FIELD: user_id
  user_id: {
    // type: "user" ensures SmartUserDisplay is used in table cells
    type: "user",
    label: "Created By",
    formSpan: 1,
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "communications_user_id_fkey",
      onDelete: "CASCADE",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Created by user",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "exact",
    width: 180,
    minWidth: 140,
    maxWidth: 260,
    icon: "User",
    mobileDetailSectionId: "system",
    groupable: true,
  },
  // FIELD: occurred_at
  occurred_at: {
    type: "datetime",
    label: "Occurred At",
    formSpan: 1,
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    mobileDetailSectionId: "timing",
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "occurred_at",
    savedViewMembership: [
      "all-communications",
      "recent-communications",
      "type-email",
      "type-note",
      "type-call",
      "type-meeting",
      "outbound-communications",
      "inbound-communications",
    ],
  },
  // FIELD: created_at
  created_at: {
    type: "datetime",
    label: "Created",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    formSpan: 1,
    mobileDetailSectionId: "timing",
    autoFill: true,
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
  },
  // FIELD: updated_at
  updated_at: {
    type: "datetime",
    label: "Updated",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    formSpan: 1,
    mobileDetailSectionId: "timing",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
  },
  // FIELD: metadata
  metadata: {
    type: "json",
    label: "Metadata",
    formSpan: 3,
    sortable: false,
    filterable: false,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 260,
    minWidth: 200,
    maxWidth: 420,
    icon: "Braces",
    mobileDetailSectionId: "system",
  },
});
